import React, { useState, useEffect } from 'react';
import { CheckCircle, Circle, Clock, FileText, AlertCircle, ArrowRight, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../services/api';

const stepColors = {
  start: { bg: 'bg-green-500', border: 'border-green-500', text: 'text-green-600' },
  action: { bg: 'bg-blue-500', border: 'border-blue-500', text: 'text-blue-600' },
  decision: { bg: 'bg-amber-500', border: 'border-amber-500', text: 'text-amber-600' },
  waiting: { bg: 'bg-gray-500', border: 'border-gray-500', text: 'text-gray-600' },
  end: { bg: 'bg-red-500', border: 'border-red-500', text: 'text-red-600' }
};

const ParcoursProgress = ({ dossierId, caisseNom, caisseId, etapeActuelle, onUpdateEtape }) => {
  const [parcours, setParcours] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [orderedSteps, setOrderedSteps] = useState([]);

  useEffect(() => {
    if (caisseId || caisseNom) {
      loadParcours();
    }
  }, [caisseId, caisseNom]);

  const loadParcours = async () => {
    try {
      setLoading(true);
      
      // Récupérer l'ID de la caisse si on n'a que le nom
      let orgId = caisseId;
      if (!orgId && caisseNom) {
        const listRes = await api.get('/parcours/organismes/list');
        const org = listRes.data.find(o => o.nom === caisseNom);
        if (org) {
          orgId = org.id;
        }
      }
      
      if (!orgId) {
        setLoading(false);
        return;
      }
      
      const res = await api.get(`/parcours/organisme/${orgId}`);
      setParcours(res.data);
      
      // Ordonner les étapes selon les edges
      if (res.data.nodes && res.data.nodes.length > 0) {
        const ordered = orderStepsByEdges(res.data.nodes, res.data.edges || []);
        setOrderedSteps(ordered);
      }
    } catch (error) {
      console.error('Erreur chargement parcours:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour ordonner les étapes selon les connexions (edges)
  const orderStepsByEdges = (nodes, edges) => {
    if (!nodes || nodes.length === 0) return [];
    
    // Trouver le nœud de départ
    const startNode = nodes.find(n => n.data?.nodeType === 'start');
    if (!startNode) {
      // Si pas de start, ordonner par position Y
      return [...nodes].sort((a, b) => (a.position?.y || 0) - (b.position?.y || 0));
    }
    
    // Construire la séquence en suivant les edges
    const ordered = [];
    const visited = new Set();
    let currentId = startNode.id;
    
    while (currentId && !visited.has(currentId)) {
      visited.add(currentId);
      const node = nodes.find(n => n.id === currentId);
      if (node) {
        ordered.push(node);
      }
      
      // Trouver l'edge qui part de ce nœud
      const nextEdge = edges.find(e => e.source === currentId);
      currentId = nextEdge?.target;
    }
    
    // Ajouter les nœuds non connectés
    nodes.forEach(n => {
      if (!visited.has(n.id)) {
        ordered.push(n);
      }
    });
    
    return ordered;
  };

  // Trouver l'index de l'étape actuelle
  const getCurrentStepIndex = () => {
    if (!etapeActuelle || orderedSteps.length === 0) return 0;
    const index = orderedSteps.findIndex(s => s.id === etapeActuelle || s.data?.label === etapeActuelle);
    return index >= 0 ? index : 0;
  };

  const currentStepIndex = getCurrentStepIndex();
  const progressPercent = orderedSteps.length > 0 
    ? Math.round((currentStepIndex / (orderedSteps.length - 1)) * 100) 
    : 0;

  // Cliquer sur une étape pour mettre à jour
  const handleStepClick = (step, index) => {
    if (onUpdateEtape) {
      onUpdateEtape(step.id, step.data?.label);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="animate-pulse flex items-center gap-3">
          <div className="w-8 h-8 bg-gray-200 rounded-full"></div>
          <div className="flex-1 h-4 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (!parcours || !parcours.nodes || parcours.nodes.length === 0) {
    return (
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-xl border border-blue-200 p-4">
        <div className="flex items-center gap-3 text-blue-700">
          <AlertCircle className="w-5 h-5" />
          <span className="text-sm">
            Aucun parcours défini pour <strong>{caisseNom}</strong>. 
            <a href="/parcours" className="ml-1 underline hover:no-underline">Configurer</a>
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden" data-testid="parcours-progress">
      {/* Header avec barre de progression */}
      <div 
        className="p-4 cursor-pointer hover:bg-gray-50 transition"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-gray-900">Progression Parcours</h3>
            <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
              {caisseNom}
            </span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-gray-600">{progressPercent}%</span>
            {expanded ? <ChevronUp className="w-5 h-5 text-gray-400" /> : <ChevronDown className="w-5 h-5 text-gray-400" />}
          </div>
        </div>
        
        {/* Barre de progression */}
        <div className="relative h-2 bg-gray-200 rounded-full overflow-hidden">
          <div 
            className="absolute inset-y-0 left-0 bg-gradient-to-r from-green-500 via-blue-500 to-blue-600 transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
        
        {/* Mini étapes */}
        <div className="flex items-center justify-between mt-3">
          {orderedSteps.map((step, index) => {
            const isCompleted = index < currentStepIndex;
            const isCurrent = index === currentStepIndex;
            const colors = stepColors[step.data?.nodeType] || stepColors.action;
            
            return (
              <div key={step.id} className="flex items-center">
                <div 
                  className={`w-6 h-6 rounded-full flex items-center justify-center transition-all ${
                    isCompleted 
                      ? `${colors.bg} text-white` 
                      : isCurrent 
                        ? `border-2 ${colors.border} ${colors.text} bg-white`
                        : 'bg-gray-100 text-gray-400'
                  }`}
                >
                  {isCompleted ? (
                    <CheckCircle className="w-4 h-4" />
                  ) : (
                    <span className="text-xs font-bold">{index + 1}</span>
                  )}
                </div>
                {index < orderedSteps.length - 1 && (
                  <div className={`w-8 h-0.5 mx-1 ${index < currentStepIndex ? colors.bg : 'bg-gray-200'}`} />
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Détails étendus */}
      {expanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-3">
            {orderedSteps.map((step, index) => {
              const isCompleted = index < currentStepIndex;
              const isCurrent = index === currentStepIndex;
              const colors = stepColors[step.data?.nodeType] || stepColors.action;
              
              return (
                <div 
                  key={step.id}
                  onClick={() => handleStepClick(step, index)}
                  className={`flex items-start gap-3 p-3 rounded-lg cursor-pointer transition ${
                    isCurrent 
                      ? 'bg-white border-2 border-blue-400 shadow-sm' 
                      : isCompleted
                        ? 'bg-green-50 border border-green-200'
                        : 'bg-white border border-gray-200 hover:border-gray-300'
                  }`}
                  data-testid={`parcours-step-${step.id}`}
                >
                  {/* Indicateur */}
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                    isCompleted 
                      ? `${colors.bg} text-white` 
                      : isCurrent 
                        ? `border-2 ${colors.border} ${colors.text} bg-white`
                        : 'bg-gray-100 text-gray-400'
                  }`}>
                    {isCompleted ? (
                      <CheckCircle className="w-5 h-5" />
                    ) : (
                      <span className="font-bold">{index + 1}</span>
                    )}
                  </div>
                  
                  {/* Contenu */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`font-medium ${isCurrent ? 'text-blue-700' : isCompleted ? 'text-green-700' : 'text-gray-700'}`}>
                        {step.data?.label || 'Étape'}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${colors.bg} text-white`}>
                        {step.data?.nodeType === 'start' && 'Début'}
                        {step.data?.nodeType === 'action' && 'Action'}
                        {step.data?.nodeType === 'decision' && 'Décision'}
                        {step.data?.nodeType === 'waiting' && 'Attente'}
                        {step.data?.nodeType === 'end' && 'Fin'}
                      </span>
                    </div>
                    
                    {/* Description de l'étape */}
                    {step.data?.description && (
                      <p className="text-sm text-gray-600 mt-1">
                        {step.data.description}
                      </p>
                    )}
                    
                    {/* Délai et documents */}
                    <div className="flex items-center gap-4 mt-1 text-xs text-gray-500">
                      {step.data?.delai && (
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {step.data.delai} jours
                        </span>
                      )}
                      {step.data?.documents && step.data.documents.length > 0 && (
                        <span className="flex items-center gap-1">
                          <FileText className="w-3 h-3" />
                          {step.data.documents.length} document(s) requis
                        </span>
                      )}
                    </div>
                    
                    {/* Liste documents si étape courante */}
                    {isCurrent && step.data?.documents && step.data.documents.length > 0 && (
                      <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                        <div className="font-medium text-blue-700 mb-1">Documents à fournir :</div>
                        <ul className="list-disc list-inside text-blue-600 space-y-0.5">
                          {step.data.documents.map((doc, i) => (
                            <li key={i}>{doc}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  
                  {/* Statut */}
                  {isCurrent && (
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-medium animate-pulse">
                      En cours
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default ParcoursProgress;
